// Reachability of customers' microtech servers, as reported by the server monitor
// through GET /api/microtech/v1/microtech-servers/certificates.

import type { CertificateExpiryColor, ServerCertificateExpiry } from './certificate-expiry'

export interface ServerConnectionSummary {
  label: string
  color: CertificateExpiryColor
  details: string[]
}

const formatCheckTime = (iso: string) =>
  new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const describeConnection = (server: ServerCertificateExpiry): string => {
  const kind = server.netbird ? 'NetBird' : 'öffentlich'
  const name = server.name || server.baseUrl
  const connection = server.connection
  if (!connection) return `${name} (${kind}): noch nicht geprüft`

  const checked = `geprüft ${formatCheckTime(connection.checkedAt)}`
  if (connection.reachable) return `${name} (${kind}): erreichbar, ${checked}`
  const reason = connection.error ? `nicht erreichbar: ${connection.error}` : 'nicht erreichbar'
  return `${name} (${kind}): ${reason}, ${checked}`
}

// summarizeServerConnection condenses all servers of a customer. Any unreachable
// server makes the customer an error; servers that were never checked keep the
// summary neutral until the first check has run.
export const summarizeServerConnection = (
  servers: ServerCertificateExpiry[] | undefined,
): ServerConnectionSummary | null => {
  if (!servers || servers.length === 0) return null

  const details = servers.map(describeConnection)
  const checked = servers.filter((s) => s.connection)
  const unreachable = checked.filter((s) => !s.connection?.reachable)

  if (unreachable.length > 0) {
    return {
      label: `${unreachable.length} von ${servers.length} nicht erreichbar`,
      color: 'error',
      details,
    }
  }
  if (checked.length < servers.length) {
    return { label: 'unbekannt', color: 'neutral', details }
  }
  return { label: 'erreichbar', color: 'success', details }
}
